import { useNinetailed } from '@ninetailed/experience.js-next';
import { useNinetailedAbTestContext } from 'contexts/ninetailed-ab-test';
import { IDynamicBundleDiscount, IDynamicBundleDiscountFields, IGlobalData } from 'global';
import { useEffect, useMemo, useRef } from 'react';
import { MyVariant, parseExperiences } from 'utils/ninetailed';

type DynamicBundleDiscount = MyVariant<IDynamicBundleDiscountFields>;

const getDynamicBundleDiscountEntries = (globalData?: IGlobalData): IDynamicBundleDiscount[] => {
  const entries = globalData?.fields.globalDiscount;
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries.filter(
    (entry): entry is IDynamicBundleDiscount =>
      entry?.sys.contentType.sys.id === 'dynamicBundleDiscount'
  );
};

/** Pushes each `dynamicBundleDiscount` entry's resolved experience to the `ninetailed` header. */
const useDynamicBundleDiscountHeaders = (globalData?: IGlobalData): void => {
  const ninetailed = useNinetailed<DynamicBundleDiscount, { id: string }>();
  const { setToHeader, deleteHeader } = useNinetailedAbTestContext();

  const entries = useMemo(() => getDynamicBundleDiscountEntries(globalData), [globalData]);

  // A callback is not a dep array: the SDK re-emits per flush, and this outlives re-subscription.
  const pushedByEntryId = useRef<Record<string, { experienceId: string; variant: number }>>({});

  useEffect(() => {
    const unsubscribes = entries.map((entry) => {
      const baseline = { ...entry.fields, id: entry.sys.id };

      return ninetailed.onSelectVariant(
        { baseline, experiences: parseExperiences({ entry }) },
        ({ experience, variantIndex }) => {
          const entryId = entry.sys.id;
          const experienceId = experience?.id;
          const pushed = pushedByEntryId.current[entryId];

          if (!experienceId && pushed) {
            delete pushedByEntryId.current[entryId];
            deleteHeader({ experienceId: pushed.experienceId });
            return;
          }

          if (!experienceId || variantIndex === undefined) {
            return;
          }

          if (pushed?.experienceId === experienceId && pushed?.variant === variantIndex) {
            return;
          }

          pushedByEntryId.current[entryId] = { experienceId, variant: variantIndex };
          setToHeader({ experienceId, variant: variantIndex });
        }
      );
    });

    return () => unsubscribes.forEach((unsubscribeFromEntry) => unsubscribeFromEntry());
  }, [deleteHeader, entries, ninetailed, setToHeader]);
};

export default useDynamicBundleDiscountHeaders;
